import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { getCurrentUserId } from '../../../user/currentUserSelector';
import { getTimestamp } from '../../../../common/utils/timeUtil';
import { replaceAssignmentList } from '../../../../common/tiptap/extensions/taskList/taskListItemAssignmentUtils';

// Types
import type { TaskItemAssignment } from '../../../../common/tiptap/extensions/taskList/taskListItemChipTypes';
import type { UpdateTaskItemAttrs } from '../taskItemChipsUtils';

type AssignmentTaskItemChipCallbacks = {
    onAssign: (assignee: { id: string; name: string }) => void;
    onRemove: () => void;
};

/**
 * Assignment callbacks for task-item chips.
 *
 * Assigning records who assigned the task and when it happened alongside the assignee, as the
 * element `assignTask` does - a task item holds an assignment in the same shape an element does,
 * so it records the same things. Picking the current assignee again is left alone by
 * `replaceAssignmentList`, so the recorded assigner and time are the first ones, not the latest.
 *
 * Deleting an assignment (or the empty "Assign to" placeholder) clears the assignees and turns off
 * the slot, mirroring the element `removeTaskAssignments` (`showAssignments: false`,
 * `assignments: []`).
 */
export const useAssignmentTaskItemChipCallbacks = ({
    assignments,
    updateAttributes,
    canModifyAssignments,
}: {
    assignments: TaskItemAssignment[];
    updateAttributes: UpdateTaskItemAttrs;
    canModifyAssignments: boolean;
}): AssignmentTaskItemChipCallbacks => {
    const currentUserId = useSelector(getCurrentUserId);

    return useMemo(
        () => ({
            onAssign: ({ id, name }: { id: string; name: string }) => {
                if (!canModifyAssignments) return;

                updateAttributes({
                    assignments: replaceAssignmentList(assignments, {
                        assigneeId: id,
                        assigneeName: name,
                        assignerId: currentUserId,
                        assigmentTimestamp: getTimestamp(),
                    }),
                    showAssignments: true,
                });
            },
            onRemove: () => {
                if (!canModifyAssignments) return;

                updateAttributes({ assignments: [], showAssignments: false });
            },
        }),
        [assignments, canModifyAssignments, currentUserId, updateAttributes],
    );
};
